import { html } from 'lit';
import { DataTableConfig, ColumnDefinition, FilterDefinition, ActionDefinition } from '../../common/data-table/index.js';

interface Location {
  id: string;
  location_id?: string;
  name: string;
  description?: string;
  address?: string;
  location_type: string;
  is_active: boolean;
  company_id?: string;
  updated_at?: number;
}

export function createLocationsTableConfig(
  locations: Location[],
  handlers: {
    onRowClick: (location: Location) => void;
    onDelete: (location: Location) => void;
    onAddLocation: () => void;
    onFilterChange?: (filters: Record<string, any>) => void;
    onSort?: (column: string, direction: 'asc' | 'desc') => void;
  }
): DataTableConfig<Location> {

  const getTypeBadge = (locationType: string) => {
    const typeMap: Record<string, string> = {
      'Office': 'primary',
      'Warehouse': 'neutral',
      'Production': 'warning',
      'Maintenance': 'danger',
      'Quality_Control': 'success',
      'Storage': 'neutral',
      'Shipping': 'primary',
      'Receiving': 'primary',
      'Other': 'neutral'
    };
    
    const variant = typeMap[locationType] || 'neutral';
    const displayName = locationType.replace('_', ' ');
    
    return html`<sl-badge pill variant=${variant}>${displayName}</sl-badge>`;
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive 
      ? html`<sl-badge pill variant="success">Active</sl-badge>`
      : html`<sl-badge pill variant="neutral">Inactive</sl-badge>`;
  };

  const columns: ColumnDefinition<Location>[] = [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      sortable: true,
      className: 'name-cell'
    },
    {
      key: 'location_type',
      label: 'Type',
      type: 'custom',
      sortable: true,
      renderer: (value: string) => getTypeBadge(value)
    },
    {
      key: 'is_active',
      label: 'Status',
      type: 'custom',
      sortable: true,
      renderer: (value: boolean) => getStatusBadge(value)
    }
  ];

  const filters: FilterDefinition[] = [
    {
      key: 'location_type',
      type: 'select',
      label: 'Type',
      placeholder: 'All Types',
      options: [
        { value: 'Office', label: 'Office' },
        { value: 'Warehouse', label: 'Warehouse' },
        { value: 'Production', label: 'Production' },
        { value: 'Maintenance', label: 'Maintenance' },
        { value: 'Quality_Control', label: 'Quality Control' },
        { value: 'Storage', label: 'Storage' },
        { value: 'Shipping', label: 'Shipping' },
        { value: 'Receiving', label: 'Receiving' },
        { value: 'Other', label: 'Other' }
      ]
    },
    {
      key: 'is_active',
      type: 'select',
      label: 'Status',
      placeholder: 'All Status',
      options: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' }
      ]
    }
  ];

  const rowActions: ActionDefinition<Location>[] = [
    {
      key: 'delete',
      label: 'Delete',
      variant: 'danger',
      icon: 'trash',
      size: 'medium',
      onClick: (location: Location, e: Event) => {
        e.stopPropagation();
        handlers.onDelete(location);
      }
    }
  ];

  const headerActions: ActionDefinition[] = [
    {
      key: 'add',
      label: 'Add Location',
      variant: 'primary',
      icon: 'plus',
      size: 'medium',
      onClick: () => handlers.onAddLocation()
    }
  ];

  return {
    columns,
    data: locations,
    filters,
    rowActions,
    headerActions,
    
    searchPlaceholder: 'Search locations...',
    pagination: {
      enabled: true,
      dynamic: true
    },
    emptyState: {
      icon: 'map-pin',
      message: 'No locations found. Click "Add Location" to create your first location.'
    },
    clickableRows: true,
    defaultSort: {
      key: 'name',
      direction: 'asc'
    },
    defaultFilters: {
      is_active: 'true' // Default to "Active" status to exclude inactive locations
    },
    onRowClick: handlers.onRowClick,
    onFilterChange: handlers.onFilterChange,
    onSort: handlers.onSort
  };
}
